import { useEffect, useState } from "react";
import { Pencil, Trash2, UserPlus, X } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const emptyUser = { name: "", email: "", password: "", role: "vendedor" };

export default function UserManagementPanel() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState(emptyUser);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState("");

  const loadUsers = () => {
    return api.get("/auth/users").then((response) => setUsers(response.data));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const createUser = async (event) => {
    event.preventDefault();
    try {
      await api.post("/auth/users", newUser);
      setNewUser(emptyUser);
      setMessage("Usuario creado correctamente.");
      loadUsers();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    const payload = { name: editing.name, email: editing.email, role: editing.role };
    if (editing.password) payload.password = editing.password;
    try {
      await api.put(`/auth/users/${editing.id}`, payload);
      setEditing(null);
      setMessage("Usuario actualizado correctamente.");
      loadUsers();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const deleteUser = async (item) => {
    if (!window.confirm(`¿Eliminar el usuario ${item.name}?`)) return;
    try {
      await api.delete(`/auth/users/${item.id}`);
      setMessage("Usuario eliminado.");
      loadUsers();
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  return (
    <section className="admin-panel users-panel" data-testid="users-admin-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">ACCESO</span>
          <h2>Usuarios</h2>
        </div>
      </div>
      <form className="user-form" data-testid="user-create-form" onSubmit={createUser}>
        <input
          data-testid="user-name-input"
          onChange={(event) => setNewUser({ ...newUser, name: event.target.value })}
          placeholder="Nombre"
          required
          value={newUser.name}
        />
        <input
          data-testid="user-email-input"
          onChange={(event) => setNewUser({ ...newUser, email: event.target.value })}
          placeholder="Correo"
          required
          type="email"
          value={newUser.email}
        />
        <input
          data-testid="user-password-input"
          minLength="8"
          onChange={(event) => setNewUser({ ...newUser, password: event.target.value })}
          placeholder="Contraseña"
          required
          type="password"
          value={newUser.password}
        />
        <select
          data-testid="user-role-select"
          onChange={(event) => setNewUser({ ...newUser, role: event.target.value })}
          value={newUser.role}
        >
          <option value="vendedor">Vendedor</option>
          <option value="supervisor">Supervisor</option>
          <option value="administrador">Administrador</option>
        </select>
        <button className="primary-button" data-testid="user-save-button" type="submit">
          <UserPlus size={16} /> Crear
        </button>
      </form>
      {message && <p className="form-note" data-testid="user-management-message">{message}</p>}
      <div className="user-list">
        {users.map((item) => (
          <div data-testid={`user-row-${item.id}`} key={item.id}>
            <strong>{item.name}</strong>
            <span>{item.email}</span>
            <small>{item.role}</small>
            <div className="user-row-actions">
              <button
                aria-label="Editar usuario"
                className="icon-button"
                data-testid={`edit-user-${item.id}`}
                onClick={() => setEditing({ ...item, password: "" })}
                type="button"
              >
                <Pencil size={15} />
              </button>
              <button
                aria-label="Eliminar usuario"
                className="icon-button danger"
                data-testid={`delete-user-${item.id}`}
                disabled={item.id === currentUser.id}
                onClick={() => deleteUser(item)}
                type="button"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <div className="user-edit-modal" data-testid="user-edit-modal">
          <form className="user-edit-form" data-testid="user-edit-form" onSubmit={saveEdit}>
            <button
              aria-label="Cerrar edición"
              className="icon-button modal-close"
              data-testid="close-user-edit"
              onClick={() => setEditing(null)}
              type="button"
            >
              <X size={18} />
            </button>
            <span className="eyebrow">EDITAR USUARIO</span>
            <h2>{editing.name}</h2>
            <label htmlFor="edit-user-name">Nombre</label>
            <input
              data-testid="edit-user-name-input"
              id="edit-user-name"
              onChange={(event) => setEditing({ ...editing, name: event.target.value })}
              required
              value={editing.name}
            />
            <label htmlFor="edit-user-email">Correo</label>
            <input
              data-testid="edit-user-email-input"
              id="edit-user-email"
              onChange={(event) => setEditing({ ...editing, email: event.target.value })}
              required
              type="email"
              value={editing.email}
            />
            <label htmlFor="edit-user-role">Rol</label>
            <select
              data-testid="edit-user-role-select"
              id="edit-user-role"
              onChange={(event) => setEditing({ ...editing, role: event.target.value })}
              value={editing.role}
            >
              <option value="vendedor">Vendedor</option>
              <option value="supervisor">Supervisor</option>
              <option value="administrador">Administrador</option>
            </select>
            <label htmlFor="edit-user-password">Nueva contraseña</label>
            <input
              data-testid="edit-user-password-input"
              id="edit-user-password"
              minLength="8"
              onChange={(event) => setEditing({ ...editing, password: event.target.value })}
              placeholder="Dejar vacío para mantener"
              type="password"
              value={editing.password}
            />
            <button className="primary-button" data-testid="save-user-edit-button" type="submit">
              Guardar cambios
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
